import {TieredMenu, TieredMenuPassThroughOptions} from 'primereact/tieredmenu';
import './tiered-dropdown.css';
import { useState } from 'react';
import { useDeleteAnswerMutation, useGetSurveyQuery } from '../../redux/survey/survey.endpoints';
import { setSaveStateWithDelay } from '../../redux/survey/survey-status.utils';
import { useDispatch } from 'react-redux';
import { SaveStatusEnum, SurveyStatus, SurveyToast } from '../../redux/survey/survey.types';
import { AnswerPixel } from '../pixel-input/answer-pixel';

interface TieredDropdownProps {
  extSurveyId: string;
  isFinalQuestion: boolean;
  isMultiSelect: boolean;
  questionId: string;
  currentQuestion: number;
  answerId: string;
  jumpToIndex: number;
  jumpToToastId: string;
  totalQuestion: number;
  handleJumpTo: (jumpIndex: number, reset?: boolean) => void;
  handleJumpToToast: (toastId: string, reset?: boolean) => void;
  answerTitle: string;
  answerPixel?: string;
  toggleMenu: () => void;
  canDelete: boolean;
  canBranchOrEnd: boolean;
  toasts: SurveyToast[];
}

interface MenuItem {
  label: string,
  icon?: string,
  command?: () => void,
  items?: MenuItem[],
}

export const TieredDropdown: React.FC<TieredDropdownProps> = ({ extSurveyId, currentQuestion, jumpToIndex, jumpToToastId, questionId, totalQuestion, answerId, handleJumpTo, handleJumpToToast, isFinalQuestion, isMultiSelect, answerTitle, answerPixel, toggleMenu, canDelete, canBranchOrEnd, toasts }) => {
  const [selectedItem, setSelectedItem] = useState<string | null>(() =>
    jumpToToastId || (jumpToIndex !== -1 ? `Question ${jumpToIndex + 1}` : (isFinalQuestion ? "End Survey" : null))
  );
  const { data } = useGetSurveyQuery(
    { ext_survey_id: extSurveyId },
    { skip: !extSurveyId }
  );
  const dispatch = useDispatch();
  const [deleteAnswer] = useDeleteAnswerMutation();
  const [isDisplayingNested, setIsDisplayingNested] = useState(false);
  const [showPixelInput, setShowPixelInput] = useState(false);
  const pt: TieredMenuPassThroughOptions = {
    root: {
      className: `${isDisplayingNested ? 'right-[88px]' : 'right-0'}`,
    },
    submenu: {
      className: 'w-[140px] min-w-[140px]',
    },
  };

  const handleDelete = async() => {
    try {
      await deleteAnswer({ ext_survey_id: extSurveyId, question_id: questionId, answer_id: answerId });
    } catch (error) {
      console.error('Error deleting answer:', error);
    }
    setSaveStateWithDelay(
      dispatch,
      SaveStatusEnum.JustSynced,
      SaveStatusEnum.Synced,
      5000
    );
  };

  const handleItemClick = (index: number, label: string) => {
    if (data?.surveyStatus == SurveyStatus.Review) {
      return;
    }

    if (selectedItem === label) {
      handleJumpTo(-1, true);
      setSelectedItem(null);
    } else {
      handleJumpTo(index);
      setSelectedItem(label);
    }
    setIsDisplayingNested(false);
  };

  const handleToastSelect = (toastId: string) => {
    if (data?.surveyStatus === SurveyStatus.Review) {
      return;
    }

    if (selectedItem === toastId) {
      handleJumpToToast("", true);
      setSelectedItem(null);
    } else {
      handleJumpToToast(toastId);
      setSelectedItem(toastId);
    }
    setIsDisplayingNested(false);
  };

  const getItemIcon = (label: string) => {
    return selectedItem === label ? 'pi pi-check' : '';
  };

  const generateItems = (total: number): MenuItem[] => {
    const items: MenuItem[] = [];

    const jumpToItems = canBranchOrEnd ? Array.from({ length: totalQuestion - currentQuestion - 2 }, (_, index) => ({
      label: `Question: ${currentQuestion + index + 3}`,
      icon: getItemIcon(`Question: ${currentQuestion + index + 3}`),
      command: () => handleItemClick(currentQuestion + index + 2, `Question: ${currentQuestion + index + 3}`),
    })) : [];

    const toastItems = !isMultiSelect ? toasts.map(toast => ({
      label: `Toast: ${toast.name}`,
      icon: getItemIcon(toast.id),
      command: () => handleToastSelect(toast.id),
    })) : [];

    const endSurveyItem = (canBranchOrEnd && currentQuestion < totalQuestion - 1) ? [{
      label: 'End Survey',
      icon: getItemIcon('End Survey'),
      command: () => handleItemClick(total, 'End Survey'),
    }] : [];

    const divider = ((jumpToItems.length || endSurveyItem.length) && toastItems.length) ? [{ label: '', className: 'h-0 p-0 overflow-hidden cursor-default border-b border-gray-400' }] : [];

    const subMenuItems = [...jumpToItems, ...endSurveyItem, ...divider, ...toastItems];

    if (subMenuItems.length > 0) {
      items.push({
        label: 'Branch to ? or Toast',
        command: () => setIsDisplayingNested(!isDisplayingNested),
        items: subMenuItems,
      });
    }

    items.push({
      label: 'Answer Pixel',
      command: () => {
        setShowPixelInput(!showPixelInput);
      },
    });

    if (canDelete) {
      items.push({
        label: 'Delete',
        command: handleDelete,
      });
    }

    return items;
  };

  const handlePixelSaved = () => {
    setShowPixelInput(false);
    toggleMenu();
  };

  return (
    <div className="tiered-menu">
      <TieredMenu
        model={generateItems(totalQuestion)}
        pt={pt}
      />
      {showPixelInput && (
        <div className="absolute right-0 z-50 w-96 shadow-lg">
          <AnswerPixel
            answerId={answerId}
            extSurveyId={extSurveyId}
            questionId={questionId}
            initialPixel={answerPixel}
            answerTitle={answerTitle}
            onClose={handlePixelSaved}
          />
        </div>
      )}
    </div>
  );
};
